import React, { useState, useEffect } from "react";
import { Stack, Typography, Box, OutlinedInput, Button, Chip } from "@mui/material";
import userAxios from "../../../../utils/axios/v1/userAxios";
import { useNotification } from "../../../../contexts/NotificationContext";
import * as Yup from "yup";
import { Formik } from "formik";

import { ReactComponent as OriginIcon } from "../../../../vendor/_new/icon/sites/origin.svg";
import { ReactComponent as DomainIcon } from "../../../../vendor/_new/icon/sites/manage_domain.svg";
import { ReactComponent as WWWIcon } from "../../../../vendor/_new/icon/sites/www.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ShieldIcon } from "../../../../vendor/_new/icon/detections/waf_detection.svg";

import colors from "../../../../utils/colors";
import useSite from "../../../../hooks/user/useSite";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";

const SectionCard = ({ icon: Icon, title, children }) => (
  <Box
    sx={{
      backgroundColor: colors.neutral[50],
      borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`,
      overflow: "hidden",
    }}
  >
    <Stack 
      direction="row" 
      alignItems="center" 
      spacing={1.5}
      sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}
    >
      {Icon && <Icon style={{ width: 20, height: 20, color: "#6371F7" }} />}
      <Typography sx={ { fontSize: 15, fontWeight: "medium", color: colors.neutral[1000] }}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ p: 3 }}>
      {children}
    </Box>
  </Box>
);

const ConfigRow = ({ icon: Icon, label, children, description }) => (
  <Stack 
    direction="row" 
    alignItems="center" 
    justifyContent="space-between" 
    sx={{ 
      py: 2,
      borderBottom: `1px solid ${colors.neutral[100]}`,
      "&:last-child": { borderBottom: "none", pb: 0 },
      "&:first-of-type": { pt: 0 },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
      {Icon && (
        <Box sx={{ 
          width: 36, 
          height: 36, 
          borderRadius: "8px", 
          backgroundColor: colors.neutral[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <Icon style={{ width: 18, height: 18, color: colors.neutral[600] }} />
        </Box>
      )}
      <Stack spacing={0.25}>
        <Typography sx={ { fontSize: 14, color: colors.neutral[1000] }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>
            {description}
          </Typography>
        )}
      </Stack>
    </Stack>
    <Box>{children}</Box>
  </Stack>
);

const StatusSwitch = ({ checked, onChange, name, disabled = false }) => (
  <Stack direction="row" alignItems="center" spacing={2}>
    <Chip
      label={checked ? "Active" : "Disabled"}
      size="small"
      sx={{
        backgroundColor: checked ? "#D1FAE5" : "#FEE2E2",
        color: checked ? "#065F46" : "#B91C1C",
        fontSize: 12,
        fontWeight: "medium",
        height: 24,
        minWidth: 70,
      }}
    />
    <Box
      onClick={() => {
        if (disabled || !onChange) return;
        onChange({ target: { name, type: "checkbox", checked: !checked } });
      }}
      sx={{
        width: 44,
        height: 24,
        borderRadius: "12px",
        backgroundColor: checked ? "#6371F7" : colors.neutral[300],
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background-color 0.2s",
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: "#fff",
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </Box>
  </Stack>
);


// Stateless API Security toggle. The parent ManageDomainConfig owns the
// Formik state for `api_security_enabled` and fires the PUT to
// /asec/:site/master from the form's onSubmit handler — matching the
// behaviour of every other switch on the page (HTTPS Redirect, www,
// HTTP Rewrite, etc.). One "Save Changes" click commits everything.
function ApiSecurityToggle({ checked, onChange, canEdit, loading }) {
  return (
    <StatusSwitch
      checked={!!checked}
      disabled={!canEdit || loading}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

const ManageDomainConfig = ({ site, handleManageDomainClose, canEdit: canEditProp }) => {
  const { setWebsiteController } = useAuth();
  const { updateSite, getSites, getSitesForList } = useSite();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  // Tracks the server-side master state so onSubmit can detect changes
  // and skip the PUT when nothing changed. Loaded on dialog open.
  const [apiSecLoaded, setApiSecLoaded] = useState(false);
  const [apiSecInitial, setApiSecInitial] = useState(false);

  useEffect(() => {
    if (!site?.id) return;
    let cancelled = false;
    userAxios.get(`/asec/${site.id}/master`)
      .then((r) => {
        if (cancelled) return;
        const enabled = !!r.data?.enabled;
        setApiSecInitial(enabled);
      })
      .catch(() => {
        if (!cancelled) setApiSecInitial(false);
      })
      .finally(() => {
        if (!cancelled) setApiSecLoaded(true);
      });
    return () => { cancelled = true; };
  }, [site?.id]);
  
  // Check if user has write permission for sites
  // Use passed permission or fall back to global check
  const canEdit = canEditProp !== undefined ? canEditProp : hasPermission(PERMISSIONS.SITES_WRITE);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        enable: undefined !== site?.enabled ? site.enabled : true,
        site_addr: site?.addr ? site.addr : "",
        api_security_enabled: apiSecInitial,
      }}
      validationSchema={Yup.object().shape({
        site_addr: Yup.string().required("Address is required"),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          values.submit = undefined;
          // Master API Security toggle — only PUT if it actually changed.
          // The handler is upsert-style so a no-op write would still fire
          // four pg_notifies on the data plane; skip when nothing changed.
          if (apiSecLoaded && values.api_security_enabled !== apiSecInitial) {
            await userAxios.put(`/asec/${site.id}/master`, {
              enabled: !!values.api_security_enabled,
            });
            setApiSecInitial(!!values.api_security_enabled);
          }
          // Strip api_security_enabled from the site payload — the site
          // service doesn't know about that field.
          const sitePayload = { ...values };
          delete sitePayload.api_security_enabled;
          await updateSite(site.id, sitePayload, null);
          // Refresh both site lists to ensure proper data update
          await Promise.all([
            getSitesForList(),
            getSites(setWebsiteController)
          ]);
          setMessage("Changes saved successfully");
          setMessageType("success");
          setTimeout(() => {
            handleManageDomainClose();
            setMessage("");
            resetForm();
          }, 1500);
        } catch (error) {
          const errorMessage = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: errorMessage });
          setSubmitting(false);
          setMessage(errorMessage);
          setMessageType("error");
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
        <form noValidate onSubmit={handleSubmit} style={{ height: "100%" }}>
          <Stack spacing={3} justifyContent="space-between" sx={{ height: "100%" }}>
            <Stack spacing={3}>
              {!!message && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: messageType === "success" ? "#D1FAE5" : "#FEE2E2",
                    border: `1px solid ${messageType === "success" ? "#10B981" : "#EF4444"}`,
                  }}
                >
                  <Typography sx={ { fontSize: 14, color: messageType === "success" ? "#065F46" : "#B91C1C" }}>
                    {message}
                  </Typography>
                </Stack>
              )}

              <SectionCard icon={DomainIcon} title="Domain Settings">
                <Stack spacing={0}>
                  <ConfigRow 
                    icon={OriginIcon} 
                    label="Origin Server" 
                    description="The backend server address"
                  >
                    <OutlinedInput
                      size="small"
                      name="site_addr"
                      placeholder="e.g., origin.example.com"
                      value={values.site_addr}
                      error={Boolean(touched.site_addr && errors.site_addr)}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={!canEdit}
                      sx={{
                        width: 180,
                        height: 36,
                        fontFamily: "monospace",
                        fontSize: 13,
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid ${colors.neutral[300]}`,
                          borderRadius: "8px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid ${colors.neutral[500]}`,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid #6371F7`,
                        },
                      }}
                    />
                  </ConfigRow>

                  <ConfigRow 
                    icon={ShieldIcon} 
                    label="Domain Protection"
                    description="Enable WAF protection for this domain"
                  >
                    <StatusSwitch 
                      name="enable" 
                      checked={values.enable} 
                      onChange={handleChange}
                      disabled={!canEdit}
                    />
                  </ConfigRow>
                  <ConfigRow 
                    icon={ShieldIcon} 
                    label="API Security"
                    description="Enable endpoint discovery + per-phase API protection (auth, spec validation, DLP, GraphQL)"
                  >
                    <ApiSecurityToggle
                      checked={values.api_security_enabled}
                      onChange={(v) => setFieldValue("api_security_enabled", v)}
                      canEdit={canEdit}
                      loading={!apiSecLoaded}
                    />
                  </ConfigRow>

                  <ConfigRow 
                    icon={WWWIcon} 
                    label="WWW Subdomain"
                    description="Automatically handle www prefix"
                  >
                    <StatusSwitch 
                      checked={true} 
                      disabled 
                    />
                  </ConfigRow>
                </Stack>
              </SectionCard>

              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "8px",
                  backgroundColor: "rgba(99, 113, 247, 0.08)",
                  border: "1px solid rgba(99, 113, 247, 0.2)",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <InfoIcon style={{ width: 20, height: 20, color: "#6371F7", flexShrink: 0, marginTop: 2 }} />
                  <Typography sx={ { fontSize: 13, lineHeight: 1.6, color: "#6371F7" }}>
                    {canEdit ? (
                      <>
                        Changes to domain configuration will take effect immediately after saving. 
                        Make sure your origin server is properly configured before enabling protection.
                      </>
                    ) : (
                      "You are viewing this domain in read-only mode. Contact an administrator to make changes."
                    )}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              {canEdit && (
                <Button
                  type="submit"
                  disabled={
                    (site?.addr === values.site_addr
                      && site?.enabled === values.enable
                      && values.api_security_enabled === apiSecInitial)
                    || isSubmitting
                  }
                  sx={{
                    height: 44,
                    flex: 1,
                    backgroundColor: "#6371F7",
                    borderRadius: "8px",
                    fontSize: 14,
                    color: "#fff",
                    fontWeight: "medium",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#4F5BD5",
                    },
                    "&.Mui-disabled": {
                      color: colors.neutral[500],
                      backgroundColor: colors.neutral[200],
                    },
                  }}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              )}
              <Button
                onClick={handleManageDomainClose}
                sx={{
                  height: 44,
                  flex: 1,
                  borderRadius: "8px",
                  fontSize: 14,
                  color: colors.neutral[700],
                  fontWeight: "medium",
                  textTransform: "none",
                  border: `1px solid ${colors.neutral[300]}`,
                  "&:hover": {
                    backgroundColor: colors.neutral[100],
                    border: `1px solid ${colors.neutral[400]}`,
                  },
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      )}
    </Formik>
  );
};
export default ManageDomainConfig;
